import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Heart, Quote, ArrowLeft, Users, BookOpen, Feather,
  ChevronLeft, ChevronRight, PenLine, Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1765526474510-b48265e14f3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxzb2Z0JTIwcGluayUyMGZsb3dlcnMlMjBwYXN0ZWwlMjBnZW50bGV8ZW58MHx8fHwxNzc1MjEzMjA1fDA&ixlib=rb-4.1.0&q=85",
  divider: "https://images.unsplash.com/photo-1677011448746-58c36a985dcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxzZXJlbmUlMjBvY2VhbiUyMGNhbG0lMjBob3Jpem9uJTIwaG9wZXxlbnwwfHx8fDE3NzUyMTMyMDV8MA&ixlib=rb-4.1.0&q=85",
};

const SAMPLE_STORIES = [
  { story_id: "s1", text: "Dopo la mastectomia, non riuscivo a guardarmi allo specchio. Un giorno mia figlia di 6 anni mi ha abbracciata e ha detto: 'Mamma, sei bellissima perche sei forte.' Quel giorno ho ricominciato a scrivere.", author_name: "Maria, 42 anni", is_anonymous: false, helped_count: 127, word_count: 35, created_at: "2026-03-15T10:00:00Z" },
  { story_id: "s2", text: "Ho scoperto di avere un linfoma a 28 anni. Il mondo si e fermato. Ma poi ho iniziato a scrivere lettere al mio corpo. 'Caro corpo, oggi ti chiedo scusa per tutte le volte che non ti ho ascoltato.' Scrivere e diventato il mio rifugio.", author_name: null, is_anonymous: true, helped_count: 89, word_count: 42, created_at: "2026-03-18T14:00:00Z" },
  { story_id: "s3", text: "Tre cicli di chemio. Capelli persi, forza persa, speranza quasi persa. Poi una sera ho scritto: 'Domani il sole sorgera anche per me.' L'ho appeso allo specchio. Ogni mattina lo leggevo. Oggi sono qui, in remissione.", author_name: "Giulia", is_anonymous: false, helped_count: 203, word_count: 38, created_at: "2026-03-20T09:00:00Z" },
  { story_id: "s4", text: "Non riesco ancora a parlarne a voce alta. Ma qui, scrivendo, riesco a dire che ho paura. Ho paura del prossimo controllo. Ma scrivere mi fa sentire meno sola, come se qualcuno dall'altra parte stesse leggendo e capendo.", author_name: null, is_anonymous: true, helped_count: 156, word_count: 38, created_at: "2026-03-22T16:00:00Z" },
  { story_id: "s5", text: "Il mio diario e iniziato come un quaderno bianco. Oggi e pieno di lacrime asciugate, di risate ritrovate, di giorni contati e vissuti con gratitudine. Ogni pagina scritta e stata un passo verso la guarigione.", author_name: "Laura, 55 anni", is_anonymous: false, helped_count: 178, word_count: 36, created_at: "2026-03-25T11:00:00Z" },
];

const SAMPLE_STATS = { total_stories: 247, total_words: 18420, total_helped: 891 };

const t = { bg: "#FCFAF9", card: "rgba(255,255,255,0.6)", text: "#2D2426", muted: "#736366", accent: "#D5A3AC", lilac: "#B39DDB", softBg: "#F4F0FA", pinkBg: "#FCEEF0" };

export default function OdrStoriesCommunityPage() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(SAMPLE_STATS);
  const navigate = useNavigate();
  const limit = 8;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => { fetchStories(); fetchStats(); }, [page]);

  const fetchStories = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/stories/feed?page=${page}&limit=${limit}`);
      if (res.ok) {
        const d = await res.json();
        if (d.stories && d.stories.length > 0) {
          setStories(d.stories);
          setTotal(d.total || 0);
        } else {
          setStories(SAMPLE_STORIES);
          setTotal(SAMPLE_STORIES.length);
        }
      } else {
        setStories(SAMPLE_STORIES);
        setTotal(SAMPLE_STORIES.length);
      }
    } catch (e) {
      setStories(SAMPLE_STORIES);
      setTotal(SAMPLE_STORIES.length);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/stories/stats`);
      if (res.ok) {
        const d = await res.json();
        if (d.total_stories > 0) setStats(d);
      }
    } catch (e) { /* keep sample stats */ }
  };

  const handleHelped = async (storyId) => {
    try {
      await fetch(`${API}/api/stories/helped`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ story_id: storyId }),
      });
    } catch (e) { /* silent */ }
    setStories(prev => prev.map(s =>
      s.story_id === storyId ? { ...s, helped_count: (s.helped_count || 0) + 1 } : s
    ));
    setStats(prev => ({ ...prev, total_helped: prev.total_helped + 1 }));
  };

  const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } } };
  const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
    } catch { return ""; }
  };

  return (
    <div className="min-h-screen transition-colors duration-1000" style={{ backgroundColor: t.bg, color: t.text, fontFamily: "'Manrope', sans-serif" }} data-testid="community-page">
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Manrope:wght@300;400;500;600&display=swap" rel="stylesheet" />

      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-[400px] h-[400px] rounded-full blur-3xl opacity-10" style={{ background: t.lilac, top: "10%", right: "-5%" }} />
        <div className="absolute w-[300px] h-[300px] rounded-full blur-3xl opacity-10" style={{ background: t.accent, bottom: "20%", left: "-5%" }} />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 md:px-12 lg:px-24 pt-8 pb-4">
        <button onClick={() => navigate("/stories")} className="inline-flex items-center gap-2 text-sm transition-all duration-500 hover:-translate-x-1" style={{ color: t.accent }} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Torna a ODR Stories
        </button>
      </header>

      {/* Hero with Image */}
      <section className="relative z-10 px-6 md:px-12 lg:px-24 py-12 lg:py-20" data-testid="community-hero">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div variants={fadeUp}>
            <div className="w-14 h-14 mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${t.accent}15` }}>
              <Users className="w-6 h-6" style={{ color: t.accent }} strokeWidth={1.5} />
            </div>
            <p className="text-sm tracking-[0.3em] uppercase mb-4" style={{ color: t.lilac }}>Community Empatica</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Storie di chi ha trovato la forza nelle parole
            </h1>
            <p className="text-base lg:text-lg leading-relaxed mb-8" style={{ color: t.muted }}>
              Ogni storia qui e un atto di coraggio. Leggile, lasciati ispirare,
              e se una ti ha aiutato, fallo sapere a chi l'ha scritta.
            </p>
            <button onClick={() => navigate("/stories")} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white font-medium transition-all duration-700 hover:-translate-y-1" style={{ backgroundColor: t.accent, boxShadow: `0 6px 24px ${t.accent}35` }} data-testid="write-story-hero-btn">
              <PenLine className="w-4 h-4" /> Scrivi la tua storia
            </button>
          </motion.div>
          <motion.div variants={fadeUp} className="hidden lg:block">
            <div className="rounded-3xl overflow-hidden shadow-xl" style={{ boxShadow: `0 16px 50px ${t.accent}15` }}>
              <img src={IMAGES.hero} alt="Fiori e rinascita" className="w-full h-[380px] object-cover" loading="lazy" />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Stats bar */}
      <section className="relative z-10 px-6 md:px-12 lg:px-24 pb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="max-w-4xl mx-auto">
          <div className="grid grid-cols-3 gap-4 rounded-3xl p-8" style={{ background: t.card, backdropFilter: "blur(24px)", border: `1px solid rgba(255,255,255,0.4)`, boxShadow: `0 8px 32px ${t.accent}08` }} data-testid="community-stats">
            {[
              { value: stats.total_stories, label: "Storie condivise" },
              { value: stats.total_words, label: "Parole scritte" },
              { value: stats.total_helped, label: "Persone aiutate" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold" style={{ color: t.accent, fontFamily: "'Playfair Display', serif" }}>{stat.value.toLocaleString("it-IT")}</p>
                <p className="text-xs sm:text-sm mt-1" style={{ color: t.muted }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Divider image */}
      <section className="relative z-10 px-6 md:px-12 lg:px-24 py-8">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="max-w-3xl mx-auto">
          <div className="rounded-3xl overflow-hidden shadow-lg" style={{ boxShadow: `0 12px 40px ${t.accent}10` }}>
            <img src={IMAGES.divider} alt="Serenita e speranza" className="w-full h-40 sm:h-48 object-cover" loading="lazy" />
          </div>
        </motion.div>
      </section>

      {/* Section title */}
      <section className="relative z-10 px-6 md:px-12 lg:px-24 pt-8 pb-4 text-center">
        <p className="text-sm tracking-[0.2em] uppercase mb-3" style={{ color: t.lilac }}>Le voci della community</p>
        <h2 className="text-2xl sm:text-3xl mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Ogni parola e un atto di coraggio</h2>
        <div className="w-16 h-px mx-auto mt-4" style={{ background: `linear-gradient(to right, transparent, ${t.accent}60, transparent)` }} />
      </section>

      {/* Stories Grid */}
      <section className="relative z-10 px-6 md:px-12 lg:px-24 py-8 pb-16" data-testid="stories-grid">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <Feather className="w-8 h-8 mx-auto mb-4 animate-pulse" style={{ color: t.accent }} />
              <p className="text-sm italic" style={{ color: t.muted }}>Caricamento storie...</p>
            </div>
          ) : (
            <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-6">
              {stories.map((story, i) => (
                <motion.article key={story.story_id || i} variants={fadeUp} className="rounded-3xl p-8 md:p-10 transition-all duration-700 hover:-translate-y-0.5" style={{ background: t.card, backdropFilter: "blur(24px)", border: `1px solid rgba(255,255,255,0.3)`, boxShadow: `0 4px 20px rgba(0,0,0,0.03)` }} data-testid={`community-story-${i}`}>
                  <div className="flex items-start gap-4">
                    <Quote className="w-8 h-8 flex-shrink-0 mt-1" style={{ color: `${t.lilac}60` }} strokeWidth={1.5} />
                    <div className="flex-1">
                      <p className="text-base md:text-lg leading-relaxed mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>{story.text}</p>

                      {story.enhanced_text && (
                        <div className="mb-6 pl-4 py-3" style={{ borderLeft: `2px solid ${t.lilac}40` }}>
                          <p className="text-xs uppercase tracking-wider mb-2" style={{ color: t.lilac }}>Versione valorizzata</p>
                          <p className="text-sm leading-relaxed italic" style={{ color: t.muted }}>{story.enhanced_text}</p>
                        </div>
                      )}

                      {story.mantra && (
                        <div className="mb-6 text-center py-3 rounded-2xl" style={{ background: `${t.accent}08` }}>
                          <p className="text-sm font-medium" style={{ color: t.accent, fontFamily: "'Playfair Display', serif" }}>"{story.mantra}"</p>
                        </div>
                      )}

                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <p className="text-xs font-medium" style={{ color: t.muted }}>
                            {story.is_anonymous ? "Anonimo" : story.author_name || "Anonimo"}
                          </p>
                          {story.created_at && (
                            <span className="text-xs" style={{ color: `${t.muted}80` }}>{formatDate(story.created_at)}</span>
                          )}
                        </div>
                        <button onClick={() => handleHelped(story.story_id)} className="flex items-center gap-2 text-xs px-5 py-2.5 rounded-full transition-all duration-500 hover:-translate-y-0.5 hover:shadow-md" style={{ border: `1px solid ${t.accent}30`, color: t.accent, background: `${t.accent}05` }} data-testid={`community-helped-btn-${i}`}>
                          <Heart className="w-3.5 h-3.5" /> Mi ha aiutato {story.helped_count > 0 && `(${story.helped_count})`}
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-12" data-testid="pagination">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 disabled:opacity-30" style={{ border: `1px solid ${t.accent}30`, color: t.accent }} data-testid="prev-page-btn">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm" style={{ color: t.muted }}>
                Pagina <strong style={{ color: t.accent }}>{page}</strong> di {totalPages}
              </span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 disabled:opacity-30" style={{ border: `1px solid ${t.accent}30`, color: t.accent }} data-testid="next-page-btn">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 px-6 md:px-12 lg:px-24 py-20 lg:py-28" style={{ backgroundColor: t.pinkBg }}>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-2xl mx-auto text-center">
          <motion.div variants={fadeUp}>
            <Sparkles className="w-8 h-8 mx-auto mb-6" style={{ color: t.accent }} strokeWidth={1.5} />
            <h2 className="text-2xl sm:text-3xl mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Anche la tua storia puo fare la differenza</h2>
            <p className="text-base mb-10 leading-relaxed" style={{ color: t.muted }}>
              Condividi la tua esperienza e aiuta qualcuno a sentirsi meno solo.
              Non devi essere perfetta. Devi solo essere vera.
            </p>
            <button onClick={() => navigate("/stories")} className="inline-flex items-center gap-3 px-10 py-4 rounded-full text-white font-medium transition-all duration-700 hover:-translate-y-1" style={{ backgroundColor: t.accent, boxShadow: `0 6px 24px ${t.accent}40` }} data-testid="write-story-cta">
              <PenLine className="w-5 h-5" /> Scrivi la tua storia
            </button>
          </motion.div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 md:px-12 lg:px-24 py-8 text-center">
        <p className="text-xs tracking-wider" style={{ color: `${t.muted}80` }}>
          ODR Stories — Community Empatica — Dove le parole diventano forza
        </p>
      </footer>
    </div>
  );
}
